"use client"

import { useState, useEffect, useRef } from "react"
import { Note } from "@types"
import { ImportOptions, ImportJobResult } from "../../../types"
import { ImportStatus } from "./types"
import { importNoteExport } from "lib/api/notes"
import { reuploadImages, convertLinksIntoLinkEmbeds } from "util/import"

/**
 * Custom hook for handling the Mirror import process
 * Manages import state and processes notes for import
 *
 * @param notes - Array of notes to import from Mirror
 * @param importOptions - Configuration options for the import
 * @returns Object containing state and functions for the import process
 */
export default function useMirrorImport(
  notes: Partial<Note>[],
  importOptions: ImportOptions
) {
  // Loading and error states
  const [isImportLoading, setIsImportLoading] = useState(true)
  const [importError, setImportError] = useState<string | null>(null)

  // Import progress tracking
  const [numFinished, setNumFinished] = useState(0)
  const [skippedPosts, setSkippedPosts] = useState<
    Array<{ title: string; reason?: string }>
  >([])
  const [erroredPosts, setErroredPosts] = useState<
    Array<{ title: string; reason?: string }>
  >([])

  // Import status and results
  const [importStatus, setImportStatus] = useState<ImportStatus>("UNSTARTED")
  const [importJobDetails, setImportJobDetails] =
    useState<ImportJobResult | null>(null)

  // Ref to prevent duplicate imports on re-renders
  const importInitiatedRef = useRef(false)

  // Calculate remaining posts
  const postsRemaining =
    notes.length - numFinished - skippedPosts.length - erroredPosts.length

  /**
   * Process a single note for import
   * Handles image uploading, link conversion, and API calls
   */
  const processNote = async (note: Partial<Note>) => {
    try {
      if (!note) return null

      // Reupload images to our CDN
      await reuploadImages(note, note.cover_img?.img?.src)

      // Convert links to embeds when appropriate
      await convertLinksIntoLinkEmbeds(note)

      // Call the import API endpoint
      return await importNoteExport(note, {
        overwrite: importOptions.overwrite,
        storeOnArweave: importOptions.storeOnArweave,
      })
    } catch (err) {
      console.error("Error processing note:", err)
      return {
        id: "FAILURE",
        error: err instanceof Error ? err.message : "Unknown error",
      }
    }
  }

  /**
   * Handles the entire import process for all notes
   */
  const runImport = async () => {
    try {
      setImportStatus("IN_PROGRESS")
      setIsImportLoading(true)

      let successful = 0
      let skipped = 0
      let errors = 0
      const skippedTitlesList: Array<{ title: string; reason?: string }> = []
      const errorTitlesList: Array<{ title: string; reason?: string }> = []

      // Process each note one by one
      for (const note of notes) {
        const response = await processNote(note)

        if (!response) continue

        // Handle different response cases
        if (response.id === "ALREADY_EXISTS") {
          // Post already exists and overwrite=false
          skipped++
          if (note.title) {
            skippedTitlesList.push({
              title: note.title.substring(0, 40),
              reason: "Already exists",
            })
          }
        } else if (response.id === "FAILURE") {
          // Error importing
          errors++
          if (note.title) {
            errorTitlesList.push({
              title: note.title.substring(0, 40),
              reason: "API error during import",
            })
          }
        } else {
          // Successful import
          successful++
        }
      }

      // Prepare final result object
      const result: ImportJobResult = {
        total: notes.length,
        successful,
        skipped,
        errors,
        skippedDetails: skippedTitlesList,
        errorDetails: errorTitlesList,
      }

      // Update UI state
      setNumFinished(successful)
      setSkippedPosts(skippedTitlesList)
      setErroredPosts(errorTitlesList)
      setImportJobDetails(result)
      setImportStatus("FINISHED")
    } catch (e) {
      console.error("Import error:", e)
      setImportError("Failed to import posts")
    } finally {
      setIsImportLoading(false)
    }
  }

  // Start the import process automatically when component mounts
  useEffect(() => {
    if (importInitiatedRef.current) return

    importInitiatedRef.current = true
    runImport()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, importOptions])

  return {
    isImportLoading,
    importError,
    numFinished,
    skippedPosts,
    erroredPosts,
    importStatus,
    importJobDetails,
    postsRemaining,
  }
}
